import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useApiCaller } from '../utils/ApiUtilities';
import { getCustomQuizDeleteUrl, getCustomQuizGetByIdUrl, getCustomQuizGetUrl, getCustomQuizPostUrl, getFavoriteQuestionDeleteUrl, getFavoriteQuestionPostUrl, getFavoriteQuestionUrl, getQuizPreviewCountsUrl, getQuizSubmissionDetailsUrl, getQuizSubmissionEndUrl, getQuizSubmissionStartUrl, getTagUrl } from '../utils/UrlUtilities'; // Re-using for mock tag data
import { Input } from './ui/input';
import "react-datepicker/dist/react-datepicker.css";
import { XCircle, Trash2, PlayCircle, Plus, ChevronDown, ChevronUp, Star, ClipboardListIcon, TrophyIcon } from 'lucide-react'; // Icons
import { useData } from '../contexts/DataContext';
import { useUser } from '../contexts/UserContext';
import { ALL_SUBJECTS_OPTION, BCS_LEVEL, MAX_TOTAL_QUESTIONS, QUIZ_VIEW_TYPES, sortBcsTerms } from '../utils/Constants';
import { constructQuizPreviewPayload, constructQuizSubmissionEndPayload, constructQuizSubmissionStartPayload, getCustomQuizDeletePayload, getCustomQuizPostPayload, getFavoriteQuestionPostPayload, getQuizSubmissionDetailsPayload } from '../utils/PayloadUtils';
import { buildSelectedOptionsJson, constructFavoriteQuestionIdToBoolMap, modifyQuizzesSubmissionDetails } from '../utils/BasicUtilities';
import QuizScoreLeaderboardModal from './UserQuizScoreLeaderboard';
import QuizSubmissionDetailsModal from './UserQuizSubmissionDetail';
import useLocalStorageLevel from '../contexts/levelHook';


export const AddQuizCard = ({ onClick }) => {
    return (
        <div
            onClick={onClick}
            className="kp-quiz-card"
            style={{
                padding: '1rem',
                border: '2px dashed #9ca3af', // Dashed border
                borderRadius: '0.5rem',
                backgroundColor: '#f9fafb',
                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                minHeight: '150px', // Ensure it's roughly same height as other cards
                color: '#6b7280',
                fontSize: '1.5rem',
                fontWeight: 'bold',
            }}
        >
            <Plus style={{ width: '3rem', height: '3rem', marginBottom: '0.5rem' }} />
            <span>Add New Test</span>
        </div>
    );
};

export const QuizAddUserForm = ({ onCancel, onSave }) => { // Removed editingQuiz prop
    const { userState } = useUser();
    // const { dataState } = useData();
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [durationMinutes, setDurationMinutes] = useState(60);
    const [level, setLevel, levelExtra] = useLocalStorageLevel();
    const { division: currentDivision, unit: currentUnit } = levelExtra;
    const [createdBy, setCreatedBy] = useState(userState?.email || '');
    const [type, setType] = useState('custom');
    const [quizSubjects, setQuizSubjects] = useState([]); // Maps to 'subjects' in payload
    const [maxQuestionPerSubject, setMaxQuestionPerSubject] = useState(null); // New field
    const [maxQuestionFromFavorite, setMaxQuestionFromFavorite] = useState(null); // New field
    const [maxQuestionFromWrongAnswer, setMaxQuestionFromWrongAnswer] = useState(null); // New field
    const [quizBcsTerms, setQuizBcsTerms] = useState([]);
    const [maxQuestionPerTerm, setMaxQuestionPerTerm] = useState(null);

    // New state for radio button selection
    const [selectedQuestionSource, setSelectedQuestionSource] = useState('customSubjects');

    // Availability preview / reconcile flow
    const [previewing, setPreviewing] = useState(false);
    const [reconcile, setReconcile] = useState(null); // { buckets, availableTotal } | null

    const setId = 1; // Default as per schema

    const CallApi = useApiCaller();
    const [tags, setTags] = useState([]); // All tags from API

    // Fetch all tags for dropdowns
    const fetchTags = async () => {
        try {
            const tagUrl = getTagUrl();
            const tagGetResponse = await CallApi({
                url: tagUrl,
                method: 'GET',
                token: true,
            });
            if (tagGetResponse.success) {
                setTags(tagGetResponse.data.tags);
            } else {
                setTags([]);
            }
        } catch (error) {
            console.error("Error fetching tags:", error);
            setTags([]);
        }
    };

    useEffect(() => {
        fetchTags();
    }, []);

    // All SUBJECT tags available for the current level (used by the "All Subjects"
    // shortcut and the subject dropdown).
    const allSubjectsForLevel = useMemo(
        () => tags.filter((t) => t.tag_type === 'SUBJECT' && t.level === level && t.is_current !== false).map((t) => t.name),
        [tags, level]
    );

    // How many questions this selection asks for (pure arithmetic — the hint).
    const requestedTotal = useMemo(() => {
        if (selectedQuestionSource === 'customSubjects') {
            return quizSubjects.length * (Number(maxQuestionPerSubject) || 0);
        }
        if (selectedQuestionSource === 'bcsTerms') {
            return (
                quizSubjects.length * (Number(maxQuestionPerSubject) || 0) +
                quizBcsTerms.length * (Number(maxQuestionPerTerm) || 0)
            );
        }
        return 0;
    }, [selectedQuestionSource, quizSubjects, maxQuestionPerSubject, quizBcsTerms, maxQuestionPerTerm]);

    const isMathValid = requestedTotal > 0 && requestedTotal <= MAX_TOTAL_QUESTIONS;

    // Actually creates the quiz (the original handleSave body). Reached either
    // directly (when the bank has enough) or from the reconcile dialog's
    // "Create with available" action.
    const doCreate = async () => {
        try {
            const startTime = null;
            const endTime = null;
            const customQuizPostPayload = getCustomQuizPostPayload(title, description, startTime, endTime, durationMinutes, createdBy, level, type, quizSubjects, maxQuestionPerSubject, [], maxQuestionFromFavorite, maxQuestionFromWrongAnswer, setId, selectedQuestionSource, [], quizBcsTerms, maxQuestionPerTerm, { division: currentDivision, unit: currentUnit });
            const customQuizPostUrl = getCustomQuizPostUrl();
            const response = await CallApi({
                url: customQuizPostUrl,
                method: 'POST',
                payload: customQuizPostPayload,
                token: true,
            });

            if (response.success) {
                onSave(response.data);
            } else {
                alert(`Failed to add test: ${response.message || 'Unknown error'}`);
                console.error(`Add Quiz Error:`, response);
            }
        } catch (error) {
            alert(`An error occurred while adding the test.`);
            console.error(`Add Quiz Error in catch block:`, error);
        }
    };

    // On Save, first ask the server how many questions actually exist per bucket.
    // If any bucket is short, open the reconcile dialog; otherwise create directly.
    const handleSave = async () => {
        try {
            setPreviewing(true);
            const previewUrl = getQuizPreviewCountsUrl();
            const previewPayload = constructQuizPreviewPayload(level, quizSubjects, maxQuestionPerSubject, quizBcsTerms, maxQuestionPerTerm, { division: currentDivision, unit: currentUnit });
            const response = await CallApi({ url: previewUrl, method: 'POST', payload: previewPayload, token: true });
            if (response.success) {
                const buckets = response.data?.buckets || [];
                const hasShortfall = buckets.some((b) => b.available < b.requested);
                if (hasShortfall) {
                    setReconcile({ buckets, availableTotal: response.data.available_total });
                    return;
                }
            }
            // No shortfall (or preview unavailable) → create with what was requested.
            await doCreate();
        } catch (error) {
            console.error('Quiz preview-counts error:', error);
            await doCreate(); // graceful degrade — don't block saving on a preview failure
        } finally {
            setPreviewing(false);
        }
    };

    const handleQuizSubjectSelect = (e) => {
        const selectedSubject = e.target.value;
        if (selectedSubject === ALL_SUBJECTS_OPTION) {
            setQuizSubjects(allSubjectsForLevel);
            return;
        }
        if (selectedSubject && !quizSubjects.includes(selectedSubject)) {
            setQuizSubjects(prev => [...prev, selectedSubject]);
        }
    };

    const handleRemoveQuizSubject = (subjectToRemove) => {
        setQuizSubjects(prev => prev.filter(sub => sub !== subjectToRemove));
    };

    const isSaveDisabled = previewing || !title || !durationMinutes || !level || !createdBy || !type ||
        (selectedQuestionSource === 'customSubjects' && (quizSubjects.length === 0 || !maxQuestionPerSubject || maxQuestionPerSubject <= 0 || !isMathValid)) ||
        (selectedQuestionSource === 'favoriteQuestions' && (maxQuestionFromFavorite === null || maxQuestionFromFavorite <= 0)) ||
        (selectedQuestionSource === 'wrongAnswerQuestions' && (maxQuestionFromWrongAnswer === null || maxQuestionFromWrongAnswer <= 0)) ||
        (selectedQuestionSource === 'bcsTerms' && (
            !(
                (quizSubjects.length > 0 && maxQuestionPerSubject > 0) ||
                (quizBcsTerms.length > 0 && maxQuestionPerTerm > 0)
            ) || !isMathValid
        ));

    // Live, arithmetic-only hint of how big the quiz will be (subjects/terms modes).
    const renderTotalHint = () => {
        if (selectedQuestionSource !== 'customSubjects' && selectedQuestionSource !== 'bcsTerms') return null;
        if (requestedTotal === 0) {
            return <small className="text-danger d-block mt-1" style={{ fontSize: '0.8rem' }}>Pick subjects/terms and a max so the test has at least 1 question.</small>;
        }
        if (requestedTotal > MAX_TOTAL_QUESTIONS) {
            return <small className="text-danger d-block mt-1" style={{ fontSize: '0.8rem' }}>This selection asks for {requestedTotal} questions — the maximum is {MAX_TOTAL_QUESTIONS}. Reduce your numbers.</small>;
        }
        return <small className="text-muted d-block mt-1" style={{ fontSize: '0.8rem' }}>This test will have up to {requestedTotal} question{requestedTotal === 1 ? '' : 's'} (actual may be fewer if the bank has less).</small>;
    };

    // Subjects multiselect (with an "All Subjects" shortcut) — reused by the
    // Custom Subjects and By BCS Term sources.
    const subjectsBlock = (
        <div style={{ width: '100%', marginLeft: '1.5rem' }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>Subjects {selectedQuestionSource === 'customSubjects' && <span style={{ color: 'red' }}>*</span>}</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.5rem' }}>
                {quizSubjects.map((sub) => (
                    <span
                        key={sub}
                        style={{ display: 'flex', alignItems: 'center', backgroundColor: '#ede9fe', color: '#5b21b6', fontSize: '0.875rem', padding: '0.25rem 0.5rem', borderRadius: '9999px' }}
                    >
                        {sub}
                        <button
                            type="button"
                            onClick={() => handleRemoveQuizSubject(sub)}
                            style={{ marginLeft: '0.5rem', color: '#ef4444', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                            <XCircle style={{ width: '0.75rem', height: '0.75rem', color: 'red' }} />
                        </button>
                    </span>
                ))}
            </div>
            <select
                value=""
                onChange={handleQuizSubjectSelect}
                style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)', color: '#1f2937', fontSize: '0.875rem' }}
            >
                <option value="" disabled>Add Subject to Quiz</option>
                {allSubjectsForLevel.length > 0 && <option value={ALL_SUBJECTS_OPTION}>All Subjects</option>}
                {allSubjectsForLevel
                    .filter((name) => !quizSubjects.includes(name))
                    .map((name) => (
                        <option key={name} value={name}>{name}</option>
                    ))}
            </select>
        </div>
    );

    const maxPerSubjectBlock = (
        <div style={{ width: '100%', marginLeft: '1.5rem' }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>Max Questions Per Subject {selectedQuestionSource === 'customSubjects' && <span style={{ color: 'red' }}>*</span>}</label>
            <Input
                type="number"
                placeholder="e.g., 10"
                value={maxQuestionPerSubject || ''}
                onChange={(e) => setMaxQuestionPerSubject(e.target.value === '' ? null : Number(e.target.value))}
                style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)', color: '#1f2937', fontSize: '0.875rem' }}
                min={1}
            />
        </div>
    );


    return (
        <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '0.75rem',
            boxShadow: '0 20px 45px rgba(76, 29, 149, 0.25)',
            border: '1px solid #ddd6fe',
            borderTop: '4px solid #7c3aed',
            width: '90%',
            maxWidth: '960px',
            margin: '6rem auto auto',
            maxHeight: 'calc(100vh - 120px)',
            overflowY: 'auto',
            padding: '1.5rem',
            position: 'relative'
        }}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1.5rem', textAlign: 'center', color: '#5b21b6' }}>
                Add New Custom Test
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div style={{ width: '100%' }}>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>
                        Test Title <span style={{ color: 'red' }}>*</span>
                    </label>
                    <textarea
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)', color: '#1f2937', fontSize: '0.875rem' }}
                        required
                        rows={1}
                        placeholder="Enter test title"
                    />
                </div>
                <div style={{ width: '100%' }}>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>
                        Test Description
                    </label>
                    <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)', color: '#1f2937', fontSize: '0.875rem' }}
                        rows={2}
                        placeholder="Enter test description"
                    />
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>Duration In Minutes <span style={{ color: 'red' }}>*</span></label>
                    <Input
                        type="number"
                        placeholder="Duration"
                        value={durationMinutes}
                        onChange={(e) => setDurationMinutes(Number(e.target.value))}
                        style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)', color: '#1f2937', fontSize: '0.875rem' }}
                        required
                    />
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>Test Level <span style={{ color: 'red' }}>*</span></label>
                    <div style={{
                        width: '100%', padding: '0.6rem 1rem', borderRadius: '0.5rem',
                        border: '1px solid #ddd6fe', background: '#f5f3ff', color: '#4c1d95', fontWeight: 700,
                        display: 'flex', flexWrap: 'wrap', gap: '0.35rem 0.75rem', alignItems: 'center'
                    }}>
                        <span>Level: {level || '—'}</span>
                        {currentDivision && <span>· Division: {currentDivision}</span>}
                        {currentUnit && <span>· Unit: {currentUnit}</span>}
                    </div>
                    <small style={{ display: 'block', marginTop: '0.35rem', fontSize: '0.8rem', color: '#9ca3af' }}>
                        Auto-filled from the level you selected at the top of the site; questions are scoped to it. Change it from the “Change Level” menu.
                    </small>
                </div>

                <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>Test Type <span style={{ color: 'red' }}>*</span></label>
                    <select
                        value={type}
                        onChange={(e) => setType(e.target.value)}
                        style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)', color: '#1f2937', fontSize: '0.875rem' }}
                        required
                    >
                        <option value="custom">CUSTOM</option>
                    </select>
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>Created By <span style={{ color: 'red' }}>*</span></label>
                    <select
                        value={createdBy}
                        onChange={(e) => setCreatedBy(e.target.value)}
                        style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)', color: '#1f2937', fontSize: '0.875rem' }}
                        required
                    >
                        <option value={createdBy}>{createdBy}</option>
                    </select>
                </div>

                {/* Radio buttons for question source selection */}
                <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>Select Question Source:<span style={{ color: 'red' }}>*</span></label>
                    <div style={{ display: 'flex', gap: '1rem' }}>
                        <label style={{ display: 'flex', alignItems: 'center', fontSize: '0.875rem', color: '#374151' }}>
                            <input
                                type="radio"
                                name="questionSource"
                                value="customSubjects"
                                checked={selectedQuestionSource === 'customSubjects'}
                                onChange={(e) => setSelectedQuestionSource(e.target.value)}
                                style={{ marginRight: '0.5rem', cursor: 'pointer' }}
                            />
                            Custom Subjects
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', fontSize: '0.875rem', color: '#374151' }}>
                            <input
                                type="radio"
                                name="questionSource"
                                value="favoriteQuestions"
                                checked={selectedQuestionSource === 'favoriteQuestions'}
                                onChange={(e) => setSelectedQuestionSource(e.target.value)}
                                style={{ marginRight: '0.5rem', cursor: 'pointer' }}
                            />
                            Favorite Questions
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', fontSize: '0.875rem', color: '#374151' }}>
                            <input
                                type="radio"
                                name="questionSource"
                                value="wrongAnswerQuestions"
                                checked={selectedQuestionSource === 'wrongAnswerQuestions'}
                                onChange={(e) => setSelectedQuestionSource(e.target.value)}
                                style={{ marginRight: '0.5rem', cursor: 'pointer' }}
                            />
                            Wrong Answer Questions
                        </label>
                        {/* BCS Term — only available when level=BCS. Lets users
                          * pull a random sample per term. */}
                        {level === BCS_LEVEL && (
                            <label style={{ display: 'flex', alignItems: 'center', fontSize: '0.875rem', color: '#374151' }}>
                                <input
                                    type="radio"
                                    name="questionSource"
                                    value="bcsTerms"
                                    checked={selectedQuestionSource === 'bcsTerms'}
                                    onChange={(e) => setSelectedQuestionSource(e.target.value)}
                                    style={{ marginRight: '0.5rem', cursor: 'pointer' }}
                                />
                                By BCS Term
                            </label>
                        )}
                    </div>
                </div>

                {/* Conditionally rendered sections based on radio selection */}
                {selectedQuestionSource === 'customSubjects' && (
                    <>
                        {subjectsBlock}
                        {maxPerSubjectBlock}
                        <div style={{ marginLeft: '1.5rem' }}>{renderTotalHint()}</div>
                    </>
                )}

                {selectedQuestionSource === 'favoriteQuestions' && (
                    <div style={{ width: '100%', marginLeft: '1.5rem' }}>
                        <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>Max Questions from Favorites <span style={{ color: 'red' }}>*</span></label>
                        <Input
                            type="number"
                            placeholder="e.g., 5"
                            value={maxQuestionFromFavorite || ''}
                            onChange={(e) => setMaxQuestionFromFavorite(e.target.value === '' ? null : Number(e.target.value))}
                            style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)', color: '#1f2937', fontSize: '0.875rem' }}
                            min={1}
                            required={selectedQuestionSource === 'favoriteQuestions'}
                        />
                    </div>
                )}

                {selectedQuestionSource === 'wrongAnswerQuestions' && (
                    <div style={{ width: '100%', marginLeft: '1.5rem' }}>
                        <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>Max Questions from Wrong Answers <span style={{ color: 'red' }}>*</span></label>
                        <Input
                            type="number"
                            placeholder="e.g., 5"
                            value={maxQuestionFromWrongAnswer || ''}
                            onChange={(e) => setMaxQuestionFromWrongAnswer(e.target.value === '' ? null : Number(e.target.value))}
                            style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)', color: '#1f2937', fontSize: '0.875rem' }}
                            min={1}
                            required={selectedQuestionSource === 'wrongAnswerQuestions'}
                        />
                    </div>
                )}

                {selectedQuestionSource === 'bcsTerms' && level === BCS_LEVEL && (() => {
                    const allBcsTerms = sortBcsTerms(
                        tags.filter(t => t.tag_type === 'BCS_TERM' && t.level === BCS_LEVEL).map(t => t.name)
                    );
                    const addTerm = (term) => {
                        if (term && !quizBcsTerms.includes(term)) {
                            setQuizBcsTerms([...quizBcsTerms, term]);
                        }
                    };
                    const removeTerm = (term) => {
                        setQuizBcsTerms(quizBcsTerms.filter(t => t !== term));
                    };
                    return (
                        <>
                        {subjectsBlock}
                        {maxPerSubjectBlock}
                        <div style={{ width: '100%', marginLeft: '1.5rem' }}>
                            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.25rem' }}>BCS Terms <span style={{ color: 'red' }}>*</span></label>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                {quizBcsTerms.map(term => (
                                    <span key={term} style={{ display: 'flex', alignItems: 'center', backgroundColor: '#ede9fe', color: '#5b21b6', fontSize: '0.875rem', padding: '0.25rem 0.5rem', borderRadius: '9999px' }}>
                                        {term}
                                        <button type="button" onClick={() => removeTerm(term)} style={{ marginLeft: '0.5rem', color: '#ef4444', backgroundColor: 'transparent', border: 'none', cursor: 'pointer' }}>
                                            <XCircle style={{ width: '0.75rem', height: '0.75rem', color: 'red' }} />
                                        </button>
                                    </span>
                                ))}
                            </div>
                            <select
                                className="form-select"
                                value=""
                                onChange={(e) => addTerm(e.target.value)}
                                style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', fontSize: '0.875rem' }}
                            >
                                <option value="">Add BCS term</option>
                                {allBcsTerms.filter(t => !quizBcsTerms.includes(t)).map(t => (
                                    <option key={t} value={t}>{t}</option>
                                ))}
                            </select>

                            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginTop: '0.75rem', marginBottom: '0.25rem' }}>Max Questions per Term <span style={{ color: 'red' }}>*</span></label>
                            <Input
                                type="number"
                                placeholder="e.g., 10"
                                value={maxQuestionPerTerm || ''}
                                onChange={(e) => setMaxQuestionPerTerm(e.target.value === '' ? null : Number(e.target.value))}
                                style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid #d1d5db', borderRadius: '0.375rem', boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)', color: '#1f2937', fontSize: '0.875rem' }}
                                min={1}
                                required={selectedQuestionSource === 'bcsTerms'}
                            />
                        </div>
                        <div style={{ marginLeft: '1.5rem' }}>{renderTotalHint()}</div>
                        </>
                    );
                })()}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.5rem' }}>
                    <button className="btn btn-danger"
                        onClick={onCancel}
                        style={{
                            // backgroundColor: 'red',
                            color: 'white',
                            padding: '0.75rem 1.5rem',
                            borderRadius: '0.5rem',
                            border: '1px solid #d1d5db',
                            cursor: 'pointer',
                            fontSize: '1rem',
                            fontWeight: '600'
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={isSaveDisabled}
                        style={{
                            backgroundColor: isSaveDisabled ? '#c4b5fd' : '#7c3aed',
                            color: '#ffffff',
                            padding: '0.75rem 1.5rem',
                            borderRadius: '0.5rem',
                            border: 'none',
                            cursor: isSaveDisabled ? 'not-allowed' : 'pointer',
                            fontSize: '1rem',
                            fontWeight: '600'
                        }}
                    >
                        {previewing ? 'Checking…' : 'Save Test'}
                    </button>
                </div>
            </div>

            {reconcile && (
                <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
                    <div style={{ backgroundColor: '#ffffff', borderRadius: '0.75rem', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', width: '90%', maxWidth: '520px', padding: '1.5rem' }}>
                        <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#1f2937', marginBottom: '0.75rem' }}>Not enough questions for some selections</h3>
                        <p style={{ fontSize: '0.9rem', color: '#4b5563', marginBottom: '0.5rem' }}>The question bank has fewer questions than you requested for:</p>
                        <ul style={{ fontSize: '0.875rem', color: '#374151', marginBottom: '0.75rem', paddingLeft: '1.25rem' }}>
                            {reconcile.buckets.filter(b => b.available < b.requested).map(b => (
                                <li key={b.label}><strong>{b.label}</strong>: requested {b.requested} → only {b.available} available</li>
                            ))}
                        </ul>
                        <p style={{ fontSize: '0.9rem', color: '#4b5563', marginBottom: '1.25rem' }}>
                            You can create this test with the <strong>{reconcile.availableTotal}</strong> available question{reconcile.availableTotal === 1 ? '' : 's'}, or go back and adjust your numbers.
                        </p>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                            <button
                                onClick={() => setReconcile(null)}
                                style={{ color: '#374151', backgroundColor: '#ffffff', padding: '0.6rem 1.2rem', borderRadius: '0.5rem', border: '1px solid #d1d5db', cursor: 'pointer', fontSize: '0.95rem', fontWeight: '600' }}
                            >
                                Adjust numbers
                            </button>
                            <button
                                onClick={() => { setReconcile(null); doCreate(); }}
                                style={{ backgroundColor: '#10b981', color: '#ffffff', padding: '0.6rem 1.2rem', borderRadius: '0.5rem', border: 'none', cursor: 'pointer', fontSize: '0.95rem', fontWeight: '600' }}
                            >
                                Create with {reconcile.availableTotal} available
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};